import { DateTime } from 'luxon';
import { StatusMode } from 'src/app/store/auth/auth.actions';
import { DailySummary, MeetingLog, Worklog } from 'src/models';
import { DemoSeedService } from './demo-seed.service';
import { DEMO_TIMELINE_PROJECTS } from './demo-timeline-projects';
import { MockDataStore } from './mock-data-store';

export { DEMO_TIMELINE_PROJECTS };

const DEMO_DEVICE_ID = 'demo-timeline-device';

/** Timetube ruler ends at 10 PM; keep mock bars inside the visible day. */
const TIMELINE_MOCK_DAY_END_HOUR = 22;

/** Matches `minMeetingSeconds` in timeline-helpers — shorter meetings are not drawn. */
const MIN_MEETING_SECONDS = 5 * 60;

const MEETING_APPS: readonly { app: string, appName: string }[] = [
  { app: 'zoom', appName: 'Zoom' },
  { app: 'meet', appName: 'Google Meet' },
  { app: 'teams', appName: 'Microsoft Teams' },
];

const TASK_NAMES: Partial<Record<StatusMode, readonly string[]>> = {
  computer: ['Deep work', 'Feature development', 'Code review', 'Documentation'],
  manual: ['Collaboration', 'Team sync', 'Planning session', 'Client call'],
  meeting: ['Stand-up', 'Team meeting', 'Project review'],
  paidBreak: ['Break', 'Lunch break'],
};

type TimelineDayVariant = 'present' | 'late' | 'partial' | 'absent' | 'weekend-light';

interface MockSegment {
  userId: string;
  project: { id: string, name: string };
  taskId: string;
  taskName: string;
  start: DateTime;
  seconds: number;
  mode: StatusMode;
}

function capSecondsBeforeLocalHour(cursor: DateTime, seconds: number, endHour: number): number {
  const cutoff = cursor.startOf('day').set({ hour: endHour, minute: 0, second: 0, millisecond: 0 });
  const maxSeconds = Math.floor(cutoff.diff(cursor, 'seconds').seconds);
  return Math.max(0, Math.min(seconds, maxSeconds));
}

function pushSegment(out: Worklog[], s: MockSegment): void {
  const seconds = capSecondsBeforeLocalHour(s.start, s.seconds, TIMELINE_MOCK_DAY_END_HOUR);
  if (seconds <= 0) {
    return;
  }
  out.push({
    deviceId: DEMO_DEVICE_ID,
    mode: s.mode,
    projectId: s.project.id,
    projectName: s.project.name,
    start: s.start.toUTC().toISO(),
    taskId: s.taskId,
    taskName: s.taskName,
    time: Math.floor(seconds),
    userId: s.userId,
  });
}

function pickTaskName(
  seed: DemoSeedService,
  mode: StatusMode,
  userId: string,
  isoDay: string,
  segment: string,
): string {
  const pool = TASK_NAMES[mode] || ['Work'];
  return pool[seed.pickIndex(pool.length, 'timeline-task', userId, isoDay, segment, mode)];
}

function preferredProject(userId: string, seed: DemoSeedService, isoDay: string): { id: string, name: string } {
  const mockUser = MockDataStore.getInstance().getUser(userId);
  let tagBias = 0;
  if (mockUser?.tagId === 'tag-2') {
    tagBias = 1;
  } else if (mockUser?.tagId === 'tag-3') {
    tagBias = 2;
  }
  const offset = seed.pickIndex(DEMO_TIMELINE_PROJECTS.length, 'timeline-project', userId, isoDay);
  return DEMO_TIMELINE_PROJECTS[(tagBias + offset) % DEMO_TIMELINE_PROJECTS.length];
}

function timelineDayVariant(
  userId: string,
  isoDay: string,
  weekday: number,
  seed: DemoSeedService,
): TimelineDayVariant {
  if (weekday > 5) {
    return seed.int(0, 99, 'timeline-weekend', userId, isoDay) < 25 ? 'weekend-light' : 'absent';
  }

  const roll = seed.int(0, 99, 'timeline-variant', userId, isoDay);
  const userNum = Number(userId.split('-').pop()?.replace(/\D/g, '') || 1);

  // Demo cast: user-1 present, user-2 late, user-3 partial, user-4 absent.
  if (userNum === 1) {
    return 'present';
  }
  if (userNum === 4 || roll < 8) {
    return 'absent';
  }
  if (userNum === 5 && roll < 18) {
    return 'absent';
  }
  if (userNum === 2 || (roll >= 18 && roll < 33)) {
    return 'late';
  }
  if (userNum === 3 || (roll >= 33 && roll < 48)) {
    return 'partial';
  }
  return 'present';
}

function buildDaySegments(
  seed: DemoSeedService,
  userId: string,
  day: DateTime,
  dayIndex: number,
  variant: TimelineDayVariant,
  out: Worklog[],
): void {
  const isoDay = day.toISODate();
  const project = preferredProject(userId, seed, isoDay);

  if (variant === 'absent') {
    return;
  }

  if (variant === 'weekend-light') {
    const weStartHour = 9 + seed.int(0, 3, 'timeline-we-hour', userId, isoDay);
    const weStartMin = seed.int(0, 59, 'timeline-we-min', userId, isoDay);
    const seconds = seed.int(3600, 7200, 'timeline-we-duration', userId, isoDay);
    const start = day.set({ hour: weStartHour, minute: weStartMin, second: 0, millisecond: 0 });
    pushSegment(out, {
      userId,
      project,
      taskId: `task-we-${isoDay}`,
      taskName: pickTaskName(seed, 'computer', userId, isoDay, 'weekend'),
      start,
      seconds,
      mode: 'computer',
    });
    return;
  }

  if (variant === 'partial') {
    const afternoonOnly = seed.int(0, 1, 'timeline-partial-half', userId, isoDay) === 1;
    const partialStartHour = afternoonOnly
      ? 13 + seed.int(0, 1, 'timeline-partial-pm-hour', userId, isoDay)
      : 8 + seed.int(0, 1, 'timeline-partial-am-hour', userId, isoDay);
    const partialStartMin = seed.int(0, 59, 'timeline-partial-min', userId, isoDay);
    const seconds = seed.int(10_800, 18_000, 'timeline-partial-duration', userId, isoDay);
    const mode: StatusMode = afternoonOnly ? 'manual' : 'computer';
    const start = day.set({ hour: partialStartHour, minute: partialStartMin, second: 0, millisecond: 0 });
    pushSegment(out, {
      userId,
      project,
      taskId: `task-partial-${isoDay}`,
      taskName: pickTaskName(seed, mode, userId, isoDay, 'partial'),
      start,
      seconds,
      mode,
    });
    return;
  }

  let startHour: number;
  let startMin: number;

  if (variant === 'late') {
    startHour = 10 + seed.int(0, 1, 'timeline-late-hour', userId, isoDay);
    startMin = seed.int(0, 59, 'timeline-late-min', userId, isoDay);
  } else {
    startHour = 8 + seed.int(0, 2, 'timeline-start-hour', userId, isoDay);
    startMin = seed.int(0, 59, 'timeline-start-min', userId, isoDay);
  }

  let cursor = day.set({ hour: startHour, minute: startMin, second: 0, millisecond: 0 });
  const morningSecs = variant === 'late'
    ? seed.int(5400, 9000, 'timeline-late-morning', userId, isoDay)
    : seed.int(7200, 12_600, 'timeline-morning', userId, isoDay);

  pushSegment(out, {
    userId,
    project,
    taskId: `task-am-${isoDay}`,
    taskName: pickTaskName(seed, 'computer', userId, isoDay, 'morning'),
    start: cursor,
    seconds: morningSecs,
    mode: 'computer',
  });
  cursor = cursor.plus({ seconds: morningSecs });

  const skipBreak = variant === 'late' && seed.int(0, 99, 'timeline-late-skip-break', userId, isoDay) < 40;
  if (!skipBreak) {
    const breakSecs = seed.int(900, 1800, 'timeline-break', userId, isoDay);
    pushSegment(out, {
      userId,
      project,
      taskId: `task-br-${isoDay}`,
      taskName: pickTaskName(seed, 'paidBreak', userId, isoDay, 'break'),
      start: cursor,
      seconds: breakSecs,
      mode: 'paidBreak',
    });
    cursor = cursor.plus({ seconds: breakSecs });
  }

  const afternoonMode: StatusMode = seed.int(0, 99, 'timeline-afternoon-mode', userId, isoDay) < 20
    ? 'meeting'
    : 'manual';
  const afternoonSecs = variant === 'late'
    ? seed.int(7200, 10_800, 'timeline-late-afternoon', userId, isoDay)
    : seed.int(10_800, 18_000, 'timeline-afternoon', userId, isoDay);

  pushSegment(out, {
    userId,
    project,
    taskId: `task-pm-${isoDay}`,
    taskName: pickTaskName(seed, afternoonMode, userId, isoDay, 'afternoon'),
    start: cursor,
    seconds: afternoonSecs,
    mode: afternoonMode,
  });
}

/**
 * Builds synthetic worklog segments for the My Working Patterns (timeline) widget.
 * Segments are anchored to local calendar days in `timezone` so timetube bars align with
 * report day boundaries (same as live activity/worklog data).
 */
export function generateMockWorklogsForRange(
  fromIso: string,
  toIso: string,
  userId: string,
  seed: DemoSeedService,
  timezone = 'UTC',
): Worklog[] {
  const zone = timezone || 'UTC';
  // Exclusive [from, to), same as DateRange / activity API (not inclusive midnight `to`).
  const days = eachLocalDayInRange(fromIso, toIso, zone);
  const out: Worklog[] = [];

  days.forEach((day, dayIndex) => {
    const isoDay = day.toISODate();
    const variant = timelineDayVariant(userId, isoDay, day.weekday, seed);
    buildDaySegments(seed, userId, day, dayIndex, variant, out);
  });

  return out;
}

function meetingOnComputerLog(
  log: Worklog,
  userId: string,
  isoDay: string,
  index: number,
  seed: DemoSeedService,
): MeetingLog | null {
  const available = log.time || 0;
  const pad = Math.min(300, Math.floor(available / 6));
  const maxDuration = available - pad * 2;
  if (maxDuration < MIN_MEETING_SECONDS) {
    return null;
  }

  const duration = seed.int(
    MIN_MEETING_SECONDS,
    Math.min(maxDuration, 45 * 60),
    'timeline-meeting-dur',
    userId,
    isoDay,
    index,
  );
  const maxOffset = Math.max(pad, available - duration - pad);
  const offset = seed.int(pad, maxOffset, 'timeline-meeting-off', userId, isoDay, index);
  const start = DateTime.fromISO(log.start, { zone: 'utc' }).plus({ seconds: offset });
  if (!start.isValid) {
    return null;
  }

  const app = MEETING_APPS[seed.pickIndex(MEETING_APPS.length, 'timeline-meeting-app', userId, isoDay, index)];
  const talkRatio = 0.55 + seed.float('timeline-meeting-talk', userId, isoDay, index) * 0.35;
  const talkTime = Math.floor(duration * talkRatio);
  const idleMins = Math.floor((duration - talkTime) / 60);

  return {
    userId,
    start: start.toUTC().toISO(),
    end: start.plus({ seconds: duration }).toUTC().toISO(),
    time: duration,
    newMeeting: 1,
    app: app.app,
    appName: app.appName,
    talkTime,
    idleMinsMeeting: idleMins,
    idleMinsMeetingRatio: duration > 0 ? (idleMins * 60) / duration : 0,
  };
}

function buildMockMeetingLogs(
  worklogs: Worklog[],
  fromIso: string,
  toIso: string,
  userId: string,
  seed: DemoSeedService,
  timezone: string,
): MeetingLog[] {
  const zone = timezone || 'UTC';
  const days = eachLocalDayInRange(fromIso, toIso, zone);
  const out: MeetingLog[] = [];

  for (const day of days) {
    const isoDay = day.toISODate();
    const variant = timelineDayVariant(userId, isoDay, day.weekday, seed);
    if (variant === 'absent' || variant === 'weekend-light') {
      continue;
    }

    const computerLogs = worklogs
      .filter(log => {
        const localDay = DateTime.fromISO(log.start, { zone: 'utc' }).setZone(zone).toISODate();
        return localDay === isoDay && log.mode === 'computer' && (log.time || 0) >= MIN_MEETING_SECONDS + 600;
      })
      .sort((a, b) => (b.time || 0) - (a.time || 0));

    if (!computerLogs.length) {
      continue;
    }

    const extra = variant === 'present'
      && computerLogs.length > 1
      && seed.int(0, 99, 'timeline-meeting-second', userId, isoDay) < 35;
    const count = extra ? 2 : 1;

    for (let i = 0; i < count; i++) {
      const meeting = meetingOnComputerLog(computerLogs[i], userId, isoDay, i, seed);
      if (meeting) {
        out.push(meeting);
      }
    }
  }

  return out;
}

/**
 * Calendar/mic meeting ranges for the Timeline report. Overlap computer
 * worklogs so `mergeMeetingsIntoWorklog` can carve dedicated meeting blocks.
 */
export function generateMockMeetingLogsForRange(
  fromIso: string,
  toIso: string,
  userId: string,
  seed: DemoSeedService,
  timezone = 'UTC',
): MeetingLog[] {
  const worklogs = generateMockWorklogsForRange(fromIso, toIso, userId, seed, timezone);
  return buildMockMeetingLogs(worklogs, fromIso, toIso, userId, seed, timezone);
}

function emptyDailySummary(userId: string): DailySummary {
  return {
    userId,
    computer: 0,
    manual: 0,
    mobile: 0,
    offcomputer: 0,
    paidBreak: 0,
    unpaidBreak: 0,
    paidLeave: 0,
    unpaidLeave: 0,
    meeting: 0,
    partial: 0,
    ratio: 0,
    total: 0,
  };
}

function addWorklogToSummary(summary: DailySummary, log: Worklog): void {
  const time = log.time || 0;
  switch (log.mode) {
    case 'computer':
      summary.computer += time;
      break;
    case 'manual':
      summary.manual += time;
      break;
    case 'meeting':
      summary.meeting = (summary.meeting || 0) + time;
      break;
    case 'paidBreak':
    case 'break':
      summary.paidBreak = (summary.paidBreak || 0) + time;
      break;
    case 'unpaidBreak':
      summary.unpaidBreak = (summary.unpaidBreak || 0) + time;
      break;
    case 'paidLeave':
      summary.paidLeave = (summary.paidLeave || 0) + time;
      break;
    case 'unpaidLeave':
      summary.unpaidLeave = (summary.unpaidLeave || 0) + time;
      break;
    default:
      summary.offcomputer += time;
      break;
  }
  summary.total += time;
  summary.ratio = summary.total > 0 ? summary.computer / summary.total : 0;
}

function eachLocalDayInRange(fromIso: string, toIso: string, timezone: string): DateTime[] {
  const fromDt = DateTime.fromISO(fromIso, { zone: 'utc' }).setZone(timezone).startOf('day');
  const toDt = DateTime.fromISO(toIso, { zone: 'utc' }).setZone(timezone);
  if (!fromDt.isValid || !toDt.isValid || toDt < fromDt) {
    return [];
  }

  const endExclusive = toDt.equals(toDt.startOf('day'))
    ? toDt.startOf('day')
    : toDt.startOf('day').plus({ days: 1 });

  const days: DateTime[] = [];
  for (let day = fromDt; day < endExclusive; day = day.plus({ days: 1 })) {
    days.push(day);
  }
  return days;
}

/**
 * Weekly timeline rows: one DailySummary per local day in `[from, to)`.
 * Totals come from the same seeded worklogs as the daily timeline so changing
 * weeks changes the mock data, and drilling into a day stays consistent.
 */
export function generateMockDailySummariesForRange(
  fromIso: string,
  toIso: string,
  userId: string,
  seed: DemoSeedService,
  timezone = 'UTC',
): DailySummary[] {
  const zone = timezone || 'UTC';
  const days = eachLocalDayInRange(fromIso, toIso, zone);
  const worklogs = generateMockWorklogsForRange(fromIso, toIso, userId, seed, zone);
  const meetings = buildMockMeetingLogs(worklogs, fromIso, toIso, userId, seed, zone);
  const logsByDay = new Map<string, Worklog[]>();

  for (const day of days) {
    logsByDay.set(day.toISODate(), []);
  }
  for (const log of worklogs) {
    const localDay = DateTime.fromISO(log.start, { zone: 'utc' }).setZone(zone).toISODate();
    logsByDay.get(localDay)?.push(log);
  }

  const summaries = days.map(day => {
    const summary = emptyDailySummary(userId);
    for (const log of logsByDay.get(day.toISODate()) || []) {
      addWorklogToSummary(summary, log);
    }
    return summary;
  });

  // Meeting seconds are a slice of computer time (daily-timetube subtracts
  // them from computer). Do not add them to `total` again.
  const summaryByDay = new Map(days.map((day, i) => [day.toISODate(), summaries[i]]));
  for (const meeting of meetings) {
    const localDay = DateTime.fromISO(meeting.start, { zone: 'utc' }).setZone(zone).toISODate();
    const summary = summaryByDay.get(localDay);
    if (summary) {
      summary.meeting = (summary.meeting || 0) + meeting.time;
    }
  }

  return summaries;
}
